import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  AccessManagementPermissionRecord,
  AccessManagementRolePermissionRecord,
  AccessManagementRoleRecord,
} from '@cdw-ae/common-data';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import { CdwDividerModule } from '@cdw-ae/common-ui-components-divider';
import { CdwMessageBandModule } from '@cdw-ae/common-ui-components-message-band';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';

@Component({
  selector: 'lib-cdw-role-edit',
  imports: [
    CommonModule,
    MaterialLibraryModule,
    CommonUiComponentsTextInputModule,
    CdwMessageBandModule,
    CdwButtonModule,
    CdwDividerModule,
    CdwCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './cdw-role-edit.component.html',
  styleUrls: ['./cdw-role-edit.component.scss'],
})
export class CdwRoleEditComponent implements OnInit {
  @Input() role!: AccessManagementRoleRecord | undefined;

  @Input() rolePermissions: AccessManagementRolePermissionRecord[] = [];

  @Input() permissions: AccessManagementPermissionRecord[] = [];

  @Output() cancelRoleEdit = new EventEmitter();

  @Output() submitRoleEdit = new EventEmitter();

  @Output() deleteRole = new EventEmitter();

  roleForm!: FormGroup;

  constructor(private formBuilder: FormBuilder) {}

  readonly grey300Color = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary-gray-300')
    .trim();

  readonly grey400Color = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary-gray-400')
    .trim();

  ngOnInit(): void {
    if (this.role) {
      this.roleForm = this.formBuilder.group({
        name: [this.role.name, Validators.required],
        description: [this.role.description, Validators.required],
      });

      this.permissions.forEach((permission, index) => {
        if (
          (permission.id !== undefined &&
            (!permission.children || permission.children.length === 0)) ||
          !permission.parentId
        ) {
          // Check if permission exists in rolePermissions, otherwise default first toggle to true
          const existingPermission = this.rolePermissions.find(
            (rolePermission) => rolePermission.permissionId === permission.id
          );
          const defaultValue = existingPermission || (index === 0);

          this.roleForm?.addControl(
            `slide-toggle-${permission.name}`,
            this.formBuilder.control(defaultValue)
          );
        }

        if (permission.children?.length) {
          this.addFormControlsForChildren(
            permission.children as AccessManagementPermissionRecord[],
            permission,
            true
          );
        }
      });
    } else {
      this.roleForm = this.formBuilder.group({
        name: ['', Validators.required],
        description: ['', Validators.required],
      });

      this.permissions.forEach((permission, index) => {
        if (
          (permission.id !== undefined &&
            (!permission.children || permission.children.length === 0)) ||
          !permission.parentId
        ) {
          // Turn on the first toggle by default
          const defaultValue = index === 0;
          this.roleForm?.addControl(
            `slide-toggle-${permission.name}`,
            this.formBuilder.control(defaultValue)
          );
        }

        if (permission.children?.length) {
          this.addFormControlsForChildren(
            permission.children as AccessManagementPermissionRecord[],
            permission,
            false
          );
        }
      });
    }
  }

  public onDeleteRole() {
    this.deleteRole.emit();
  }

  public onSaveRole() {
    if (this.roleForm?.controls) {
      const roleData: {
        name: string;
        description: string;
        permissions: { [key: string]: boolean };
      } = {
        name: this.roleForm.get('name')?.value ?? '',
        description: this.roleForm.get('description')?.value ?? '',
        permissions: {},
      };

      Object.entries(this.roleForm.controls).forEach(([key, control]) => {
        if (
          key !== 'name' &&
          key !== 'description' &&
          !key.startsWith('slide-toggle')
        ) {
          roleData.permissions[key] = !!control.value;
        }
      });
      this.submitRoleEdit.emit(roleData);
    }
  }

  public onCancelRole() {
    this.cancelRoleEdit.emit();
  }

  public onSlideToggleChange(
    slidePermission: AccessManagementPermissionRecord
  ) {
    const parentPermission = this.permissions.find(
      (permission) => permission.id === slidePermission.id
    );
    const slideToggleFormValue = !!this.roleForm.get(
      slidePermission.id.toString()
    )?.value;

    if (parentPermission && !slideToggleFormValue) {
      this.disableChildrenPermissions(parentPermission);
    }
  }

  private disableChildrenPermissions(
    permission: AccessManagementPermissionRecord
  ) {
    this.roleForm.get(permission.id.toString())?.setValue(undefined);

    if (permission.children?.length) {
      permission.children.forEach((child) => {
        this.disableChildrenPermissions(
          child as AccessManagementPermissionRecord
        );
      });
    }
  }

  private addFormControlsForChildren(
    children: AccessManagementPermissionRecord[],
    topLevelNode: AccessManagementPermissionRecord,
    useRolePermissions = false
  ): void {
    children.forEach((child) => {
      if (
        child.id !== undefined &&
        (!child.children || child.children.length === 0)
      ) {
        if (useRolePermissions) {
          const existingPermission: AccessManagementRolePermissionRecord | undefined =
            this.rolePermissions.find(
              (rolePermission) => rolePermission.permissionId === child.id
            );

          // Convert to boolean: true if permission exists, false otherwise
          const isChecked = !!existingPermission;

          if (existingPermission) {
            this.roleForm
              .get(`slide-toggle-${topLevelNode.name}`)
              ?.setValue(true);
          }
          this.roleForm?.addControl(
            child.id.toString(),
            this.formBuilder.control(isChecked)
          );
        } else {
          this.roleForm?.addControl(
            child.id.toString(),
            this.formBuilder.control(false)
          );
        }
      }

      if (child.children?.length) {
        this.addFormControlsForChildren(
          child.children as AccessManagementPermissionRecord[],
          topLevelNode,
          useRolePermissions
        );
      }
    });
  }
}
